// 플러그인 공통 계약 — whif-engine `Plugin`(Name·Version·Init·Close·DependsOn·ConflictsWith)의 프론트엔드 대응 (ADR-048 D2)
import type { ComponentType } from 'react';
import type { z } from 'zod';

import type { Locale } from '../../ports/i18n';
import type { PluginConfigEntry } from '../../ports/plugin-config';
import type { SlotKey } from '../../ports/slots';
import type { StageKey, StagePayloads } from '../../ports/stages';

/** 변경 훅 — priority 오름차순으로 차례로 돌고, 돌려준 값이 다음 훅의 입력이 된다 */
export type MutatingHook<S extends StageKey> = (payload: StagePayloads[S]) => StagePayloads[S] | Promise<StagePayloads[S]>;

/** 관찰 훅 — 결과에 영향 0. 얼린 사본을 받고, 돌려준 값은 버린다 */
export type ObserverHook<S extends StageKey> = (payload: Readonly<StagePayloads[S]>) => unknown;

/** setup 이 받는 기여 창구 — 확장점 4종 (ADR-048 D3). 코어가 연 자리에만 기여한다 */
export interface PluginContext {
  readonly name: string;
  /** 라우트 — 화면 경로 */
  route(path: string, component: ComponentType): void;
  /** 슬롯 — 코어가 정한 자리에 위젯. priority 오름차순으로 그린다 (기본 100) */
  slot(key: SlotKey, component: ComponentType, options?: { priority?: number }): void;
  /** 단계 훅(변경) — 기본 priority 100 */
  mutate<S extends StageKey>(stage: S, hook: MutatingHook<S>, options?: { priority?: number }): void;
  /** 단계 훅(관찰) */
  observe<S extends StageKey>(stage: S, hook: ObserverHook<S>): void;
  /** 문구 — 키는 `<플러그인>.<키>` 로 저장된다 */
  messages(locale: Locale, dict: Readonly<Record<string, string>>): void;
}

export interface PluginDefinition<P = unknown> {
  readonly name: string;
  readonly version: string;
  /** 같이 켜져 있어야 하는 플러그인 — 부팅 시 검사, 미충족이면 부팅 중단 */
  readonly dependsOn?: readonly string[];
  /** 같이 켜지면 안 되는 플러그인 — 부팅 시 검사 */
  readonly conflictsWith?: readonly string[];
  /** params 스키마 — 해소된 설정을 경계에서 파싱한다 (whif-engine SchemaProvider 대응) */
  readonly params?: z.ZodType<P>;
  /** 설정 해소의 첫 층(기본값) */
  readonly defaults?: PluginConfigEntry;
  setup(ctx: PluginContext, params: P): void;
  dispose?(): void;
}

/** 레지스트리·호스트가 다루는 플러그인 (params 타입을 지운 형태) */
export type AnyPlugin = PluginDefinition<unknown>;

export function definePlugin<P = Record<string, never>>(definition: PluginDefinition<P>): PluginDefinition<P> {
  return definition;
}

export function isPluginDefinition(value: unknown): value is AnyPlugin {
  const v = value as Partial<AnyPlugin> | null;
  return typeof v?.name === 'string' && typeof v.version === 'string' && typeof v.setup === 'function';
}
